import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles,
  HeartPulse,
  Brain,
  BookOpen,
  MessagesSquare,
  ArrowRight,
} from "lucide-react";

const BG_IMG =
  "https://static.prod-images.emergentagent.com/jobs/a4143582-6eb1-4e43-b482-b7eb9ef3a7f9/images/4b5c6aad77a5398da2e0382be1a07b3bfa387cc4fe1d5f78c42b17d6f62e3d3a.png";

const BENEFITS = [
  { icon: HeartPulse, titleKey: "landing.benefit.qol.title", blurbKey: "landing.benefit.qol.blurb" },
  { icon: Brain, titleKey: "landing.benefit.brain.title", blurbKey: "landing.benefit.brain.blurb" },
  { icon: BookOpen, titleKey: "landing.benefit.manual.title", blurbKey: "landing.benefit.manual.blurb" },
  { icon: MessagesSquare, titleKey: "landing.benefit.ai.title", blurbKey: "landing.benefit.ai.blurb" },
];

export default function Login() {
  const { login } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [showSignIn, setShowSignIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    const u = res.user;
    if (u.role === "user" && !u.onboarding_completed) navigate("/onboarding");
    else navigate("/");
  };

  return (
    <div
      className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-[#F9F8F6]"
      data-testid="landing-page"
    >
      {/* Left: pitch + CTA / sign-in */}
      <div className="flex flex-col justify-between px-6 sm:px-12 py-10 lg:py-12">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sparkles strokeWidth={1.5} className="text-[#B37A5F]" size={22} />
            <div className="leading-tight">
              <span className="font-heading text-xl tracking-tight font-semibold text-[#1A1D1A] block">
                NeuroSophia
              </span>
              <span
                className="text-xs italic text-[#5C635A]"
                data-testid="brand-tagline"
              >
                {t("brand.tagline")}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="light" />
            {!showSignIn && (
              <button
                type="button"
                onClick={() => setShowSignIn(true)}
                data-testid="show-sign-in-link"
                className="text-sm text-[#5C635A] hover:text-[#1A1D1A] underline-offset-4 hover:underline"
              >
                {t("landing.sign_in_link")}
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 flex items-center py-10">
          <div className="w-full max-w-lg">
            {showSignIn ? (
              <>
                <p className="text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold mb-4">
                  {t("login.welcome_back")}
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-[#1A1D1A] leading-[1.1]">
                  {t("login.title_part_a")}{" "}
                  <span className="font-semibold">{t("login.title_part_b")}</span>
                </h1>
                <p className="mt-4 text-[#5C635A] leading-relaxed">
                  {t("login.subtitle")}
                </p>

                <form
                  onSubmit={handleSubmit}
                  className="mt-10 space-y-6"
                  data-testid="login-form"
                >
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]"
                    >
                      {t("login.email_label")}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("login.email_placeholder")}
                      data-testid="login-email-input"
                      className="h-12 rounded-lg bg-white border-[#E5E2DC] focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="password"
                      className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]"
                    >
                      {t("login.password_label")}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("login.password_placeholder")}
                      data-testid="login-password-input"
                      className="h-12 rounded-lg bg-white border-[#E5E2DC] focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
                    />
                  </div>

                  {error && (
                    <p
                      className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
                      data-testid="login-error"
                    >
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="login-submit-button"
                    className="w-full h-12 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white font-medium tracking-tight"
                  >
                    {submitting ? t("login.submitting") : t("login.submit")}
                  </Button>
                </form>

                <p className="mt-8 text-sm text-[#5C635A]">
                  {t("login.new_here_prompt")}{" "}
                  <button
                    type="button"
                    onClick={() => setShowSignIn(false)}
                    data-testid="back-to-landing-link"
                    className="font-semibold text-[#4A5D4E] hover:underline"
                  >
                    {t("landing.back_to_landing")}
                  </button>
                </p>
              </>
            ) : (
              <>
                <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-[#1A1D1A] leading-[1.05]">
                  {t("landing.headline_a")}{" "}
                  <span className="font-semibold italic">{t("landing.headline_b")}</span>
                </h1>
                <p className="mt-5 text-[#5C635A] leading-relaxed text-base sm:text-lg max-w-md">
                  {t("landing.lead")}
                </p>

                <ul className="mt-10 space-y-4" data-testid="landing-benefits">
                  {BENEFITS.map(({ icon: Icon, titleKey, blurbKey }) => (
                    <li
                      key={titleKey}
                      className="flex items-start gap-4"
                      data-testid={`benefit-${titleKey}`}
                    >
                      <div className="h-11 w-11 rounded-xl bg-white border border-[#E5E2DC] flex items-center justify-center shrink-0">
                        <Icon strokeWidth={1.5} className="text-[#4A5D4E]" size={20} />
                      </div>
                      <div className="pt-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#1A1D1A] tracking-tight">
                            {t(titleKey)}
                          </span>
                          <span className="text-[10px] uppercase tracking-[0.18em] text-[#B37A5F] font-bold bg-[#F5EAE0] border border-[#E5D2C2] rounded-full px-2 py-0.5">
                            {t("landing.benefit.free_pill")}
                          </span>
                        </div>
                        <p className="text-sm text-[#5C635A] mt-0.5 leading-relaxed">
                          {t(blurbKey)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-4">
                  <Link
                    to="/register"
                    data-testid="landing-cta-register"
                    className="inline-flex items-center justify-center h-12 px-7 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white font-medium tracking-tight transition-colors"
                  >
                    {t("landing.cta.register")}
                    <ArrowRight strokeWidth={1.75} size={16} className="ml-2" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setShowSignIn(true)}
                    data-testid="landing-cta-sign-in"
                    className="text-sm text-[#5C635A] hover:text-[#1A1D1A] font-medium"
                  >
                    {t("landing.cta.sign_in")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-xs text-[#8A9188] leading-relaxed" data-testid="landing-copyright">
          {t("footer.copyright_prefix")}{" "}
          <a
            href="https://www.neurosofie.nl/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline-offset-2 hover:underline hover:text-[#1A1D1A]"
            data-testid="landing-copyright-link"
          >
            {t("footer.copyright_org")}
          </a>
          {t("footer.copyright_address")}
        </p>
      </div>

      {/* Right: hero image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src={BG_IMG}
          alt="Wellness organic shapes"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#4A5D4E]/10 via-transparent to-[#B37A5F]/10" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/40 p-8">
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C635A] font-bold mb-3">
              The NeuroSophia Platform ·{" "}
              <span className="italic normal-case tracking-normal">
                {t("brand.tagline")}
              </span>
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1A1D1A] font-semibold leading-tight">
              {t("landing.hero_card_title")}
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
